import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";

import { db } from "../config/firebasedeconfig";

import {
  getMembershipState,
  getForumLimits,
} from "./membershipUtils";


const DAY_MS = 24 * 60 * 60 * 1000;
const THIRTY_DAYS_MS = 30 * DAY_MS;


/**
 * Vérifie combien d'actions Forum l'utilisateur a effectuées
 * durant les dernières 24 heures et les 30 derniers jours.
 *
 * Une action Forum = création d'un sujet OU création d'une réponse.
 */
export const getForumQuotaStatus = async ({
  userId,
  subscription,
}) => {
  if (!userId) {
    return {
      allowed: false,
      reason: "not-authenticated",
      effectiveTier: "free",
      limits: getForumLimits("free"),
      dailyCount: 0,
      rolling30DaysCount: 0,
      remainingDaily: 0,
      remaining30Days: 0,
    };
  }

  const { effectiveTier } = getMembershipState(subscription);
  const limits = getForumLimits(effectiveTier);


  /*
   * Fanatique = illimité.
   *
   * Inutile d'interroger Firestore uniquement pour savoir
   * s'il peut publier.
   */
  if (
    limits.daily === Infinity &&
    limits.rolling30Days === Infinity
  ) {
    return {
      allowed: true,
      reason: null,
      effectiveTier,
      limits,
      dailyCount: 0,
      rolling30DaysCount: 0,
      remainingDaily: Infinity,
      remaining30Days: Infinity,
    };
  }


  const now = Date.now();

  const twentyFourHoursAgo = now - DAY_MS;
  const thirtyDaysAgo = now - THIRTY_DAYS_MS;


  /*
   * Une seule lecture Firestore :
   * on récupère toutes les actions des 30 derniers jours.
   *
   * Puis on détermine localement lesquelles appartiennent
   * également aux dernières 24 heures.
   */
  const usageRef = collection(
    db,
    "users",
    userId,
    "forumUsage"
  );

  const usageQuery = query(
    usageRef,
    where(
      "createdAt",
      ">=",
      Timestamp.fromMillis(thirtyDaysAgo)
    )
  );

  const usageSnapshot = await getDocs(usageQuery);


  let dailyCount = 0;
  let rolling30DaysCount = 0;


  usageSnapshot.forEach((usageDoc) => {
    const data = usageDoc.data();
    const createdAt = data?.createdAt;

    if (!createdAt || typeof createdAt.toMillis !== "function") {
      return;
    }

    const createdAtMs = createdAt.toMillis();

    rolling30DaysCount += 1;

    if (createdAtMs >= twentyFourHoursAgo) {
      dailyCount += 1;
    }
  });


  const dailyLimitReached =
    Number.isFinite(limits.daily) &&
    dailyCount >= limits.daily;

  const rolling30DaysLimitReached =
    Number.isFinite(limits.rolling30Days) &&
    rolling30DaysCount >= limits.rolling30Days;


  const allowed =
    !dailyLimitReached &&
    !rolling30DaysLimitReached;


  let reason = null;

  if (dailyLimitReached) {
    reason = "daily-limit";
  } else if (rolling30DaysLimitReached) {
    reason = "rolling-30-days-limit";
  }


  return {
    allowed,
    reason,
    effectiveTier,
    limits,

    dailyCount,
    rolling30DaysCount,

    remainingDaily: Number.isFinite(limits.daily)
      ? Math.max(limits.daily - dailyCount, 0)
      : Infinity,

    remaining30Days: Number.isFinite(
      limits.rolling30Days
    )
      ? Math.max(
          limits.rolling30Days - rolling30DaysCount,
          0
        )
      : Infinity,
  };
};